/**
 * @file EmptyState - Friendly "no data" placeholder with optional call-to-action.
 *
 * Renders a vertically centered icon, title, optional description, and an
 * optional primary action button. Used across list/table views when a query
 * returns zero results (e.g., no absences pending, no todos assigned).
 *
 * The action button only renders when BOTH `actionLabel` and `onAction` are
 * provided, preventing broken buttons from appearing if only one prop is set.
 *
 * @example
 *   <EmptyState
 *     title="Keine Abwesenheiten"
 *     message="Es liegen derzeit keine Anträge vor."
 *     actionLabel="Neue Abwesenheit"
 *     onAction={() => setDialogOpen(true)}
 *   />
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button } from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
function EmptyState({ icon, title, message, actionLabel, onAction }) {
  // Default to InboxIcon when no custom icon is supplied, keeping a consistent
  // visual language for all empty states without requiring every caller to import an icon.
  const displayIcon = icon || <InboxIcon sx={{ fontSize: 64, color: 'text.disabled' }} />;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 2,
        textAlign: 'center',
      }}
    >
      {/* Icon container with styling */}
      <Box
        sx={{
          mb: 2,
          color: 'text.disabled',
          '& > svg': {
            fontSize: 64,
          },
        }}
      >
        {displayIcon}
      </Box>

      {/* Title */}
      <Typography
        variant="h6"
        color="text.secondary"
        gutterBottom
      >
        {title}
      </Typography>

      {/* Optional message */}
      {message && (
        <Typography
          variant="body2"
          color="text.disabled"
          sx={{ maxWidth: 400, mb: actionLabel ? 3 : 0 }}
        >
          {message}
        </Typography>
      )}

      {/* Optional action button */}
      {actionLabel && onAction && (
        <Button
          variant="contained"
          color="primary"
          onClick={onAction}
        >
          {actionLabel}
        </Button>
      )}
    </Box>
  );
}

EmptyState.propTypes = {
  /** Icon to display above the title */
  icon: PropTypes.node,
  /** Main title text (required) */
  title: PropTypes.string.isRequired,
  /** Optional descriptive message */
  message: PropTypes.string,
  /** Optional action button label */
  actionLabel: PropTypes.string,
  /** Optional callback for action button click */
  onAction: PropTypes.func,
};

EmptyState.defaultProps = {
  icon: null,
  message: null,
  actionLabel: null,
  onAction: null,
};

export default EmptyState;
